import { createGuid, deepCopyObject } from "services/UtilsService";
import EventsActions from "events/EventsEmitter";
import RasterActions from "./RasterActions";
import {
    getAttributes,
    getPlaneInputValues,
    getEntitiesBounds,
    getLayerSettings,
    getAttributesValues,
    getComponentId,
} from "./GlobalActions";
import { inputsType } from "../constants/viewer";
import ViewerEvents from "events/ViewerEvents";

export default class ViewerActions extends EventsActions {
    static viewer: any;
    static viewMetaData: any = {};
    static compositeMeta: any;
    static viewBounds: any = [];
    static project: any;
    static projectId: string;
    static viewId: string;
    static clickEventHook: boolean;
    static keyEventHook: boolean;
    static skipRaycast: boolean = false;
    static pointerPressed: boolean = false;
    static pointerDownPageX: number = 0;
    static pointerDownPageY: number = 0;
    static compositeMeshes: any = [];
    static compositeRasters: any = [];
    static pendingCompositeMeshes: any = [];
    static extendedMeta: any = [];

    static init(currentViewer: any, viewMetaData: any, compositeMeta: any) {
        ViewerActions.viewer = currentViewer;
        ViewerActions.viewMetaData = viewMetaData;
        ViewerActions.compositeMeta = compositeMeta;
    }

    static destroy() {
        delete ViewerActions.viewer;
        delete ViewerActions.compositeMeta;
        delete ViewerActions.project;
        ViewerActions.viewMetaData = {};
        ViewerActions.viewBounds = [];
        ViewerActions.projectId = "";
        ViewerActions.viewId = "";
        ViewerActions.clickEventHook = false;
        ViewerActions.keyEventHook = false;
        ViewerActions.skipRaycast = false;
        ViewerActions.pointerPressed = false;
        ViewerActions.pointerDownPageX = 0;
        ViewerActions.pointerDownPageY = 0;
        ViewerActions.compositeMeshes = [];
        ViewerActions.compositeRasters = [];
        ViewerActions.pendingCompositeMeshes = [];
        ViewerActions.extendedMeta = {};
        console.log("cleanup ViewerActions done...");
    }

    static setPanelMode(mode: number, update?: boolean) {
        ViewerActions.bind(this);
    }

    static setClickEventHook(hook: boolean) {
        ViewerActions.clickEventHook = hook;
    }

    static setKeyEventHook(hook: boolean) {
        ViewerActions.keyEventHook = hook;
    }

    static onMouseDown(event: any) {
        ViewerActions.skipRaycast = false;
        ViewerActions.pointerPressed = true;
        ViewerActions.pointerDownPageX = event.pageX;
        ViewerActions.pointerDownPageY = event.pageY;
    }

    static onMouseMove(event: any) {
        if (ViewerActions.pointerPressed) {
            //Preventing Raycast During Click Event Hook, Based On A Certain Amount Of Drag
            //Note: Additionaly we could check for if the viewer is panning or rotating..
            const pointerDragThreshold = 0.02;
            const pointerDragPageX: number = event.pageX;
            const pointerDragPageY: number = event.pageY;
            const pointerDragAmountX = Math.abs(pointerDragPageX - ViewerActions.pointerDownPageX);
            const pointerDragAmountY = Math.abs(pointerDragPageY - ViewerActions.pointerDownPageY);
            if (pointerDragAmountX > pointerDragThreshold || pointerDragAmountY > pointerDragThreshold) {
                ViewerActions.skipRaycast = true;
            }
        }
    }

    static onKeyDown(event: any) {
        ViewerActions.bind(this);
    }

    static onMouseUp(event: any) {
        ViewerActions.pointerPressed = false;
    }

    static onMouseClick(event: any) {
        var element = event.target;
        if (element.tagName === "CANVAS" && ViewerActions.clickEventHook) {
            const viewer = ViewerActions.viewer;
            if (viewer && !ViewerActions.skipRaycast) {
                const raycastViewResult = viewer.raycastViewPrimitives(event.pageX, event.pageY);
                //Collecting attribute names if exist
                const layerIndex: number = raycastViewResult.layerId;
                var attributeNames: any = [];
                const entityId = getComponentId(layerIndex);
                console.log("%centityId:", "color:orange", entityId);
                if (entityId) {
                    attributeNames = getAttributes(entityId);
                }

                if (raycastViewResult !== null && raycastViewResult.valid) {
                    //Collecting attribute values if exist
                    var attributeValues: any = [];
                    var alphaAttributes = getAttributesValues(entityId) || null;
                    switch (raycastViewResult.dataType) {
                        case "surface":
                            attributeNames = [];
                            break;
                        case "point":
                        case "block":
                        case "collar":
                            break;
                        case "trace":
                            attributeValues = raycastViewResult.data.segmentAttributeValues;
                            break;
                        case "cylinder":
                            attributeValues = raycastViewResult.data.segmentAttributeValues;
                            break;
                        case "geoChemCollar":
                            attributeValues = raycastViewResult.data.collarAttributeValues;
                            break;
                    }
                    if (attributeValues.length > 0 && alphaAttributes.length > 0) {
                        attributeValues.forEach((value: number, index: number) => {
                            var alphaAttribute = alphaAttributes.find((alpha: any) => alpha.index === index);
                            if (alphaAttribute) {
                                attributeValues[index] = alphaAttribute.values[value];
                            }
                        });
                    }

                    //Firing onRaycastHit
                    const eventData: any = {
                        type: raycastViewResult.dataType,
                        data: raycastViewResult.data,
                        cellId: raycastViewResult.cellId,
                        layerId: raycastViewResult.layerId,
                        worldPoint: raycastViewResult.worldPoint,
                        customAttributeNames: attributeNames,
                        customAttributeValues: attributeValues,
                        alphaAttributes: alphaAttributes,
                    };
                    ViewerActions.onRaycastHit(eventData);
                }
            }
        }
    }

    static handleClick(event: any, done?: (object: any) => void) {
        const viewer: any = ViewerActions.viewer;
        if (!viewer) {
            return;
        }
        //console.log('event:cmd', event.cmd, '|param:', event.param);
        const { cmd, param } = event;
        if (cmd && viewer) {
            switch (cmd) {
                case "setOrtho": {
                    viewer.setOrthographicProjection(param);
                    break;
                }
                case "zoomextents": {
                    viewer.zoomToExtents();
                    break;
                }
                case "zoomregion": {
                    const regionData = param;
                    viewer.zoomToRegion(regionData);
                    break;
                }
                case "fullscreen": {
                    viewer.toggleFullscreen();
                    break;
                }
                case "zoomin": {
                    viewer.applyZoomDelta(param);
                    break;
                }
                case "zoomout": {
                    viewer.applyZoomDelta(param);
                    break;
                }
                case "pan": {
                    viewer.setNavigationMode(param);
                    break;
                }
                case "vorbit": {
                    viewer.setNavigationMode(param);
                    break;
                }
                case "updateCrossSectionX": {
                    viewer.updateCrossSectionX(param);
                    break;
                }
                case "updateCrossSectionY": {
                    viewer.updateCrossSectionY(param);
                    break;
                }
                case "updateCrossSectionZ": {
                    viewer.updateCrossSectionZ(param);
                    break;
                }
                case "updateCrossSectionW": {
                    viewer.updateCrossSectionCustom(param);
                    break;
                }
                case "updateCrossSectionPresetType": {
                    //Additionnaly we might want to show/hide custom step increment

                    const presetType = param;
                    if (presetType === 11) {
                        //Prehaps we still want to keep previous plane until completion
                        viewer.updateCrossSectionPresetType(0);

                        const onComplete = function (data: any) {
                            const worldPointA = data.wPointA;
                            const worldPointB = data.wPointB;
                            const worldPointC = viewer.getCameraWorldPosition();
                            viewer.updateCustomCrossSectionPlaneFromCoplanarPoints(
                                worldPointA,
                                worldPointB,
                                worldPointC
                            );
                            viewer.updateCrossSectionPresetType(11);
                            viewer.deleteSegment("CustomSliceSegmentId");
                        };
                        const onCancel = function (data: any) {
                            //
                        };
                        const onDelete = function (data: any) {
                            //
                        };
                        viewer.startSegment("CustomSliceSegmentId", 0, onComplete, onCancel, onDelete);
                    } else {
                        viewer.updateCrossSectionPresetType(presetType);
                        viewer.deleteSegment("CustomSliceSegmentId");
                    }
                    break;
                }
                case "updateCrossSectionWidth": {
                    viewer.updateCrossSectionWidth(param);
                    break;
                }
                case "updateCrossSectionBlockModelWidth": {
                    viewer.updateCrossSectionBlockModelWidth(param);
                    break;
                }
                case "updateCrossSectionDrillHolesWidth": {
                    viewer.updateCrossSectionDrillHolesWidth(param);
                    break;
                }
                case "updateCrossSectionXDip": {
                    viewer.updateCrossSectionXDip(param);
                    break;
                }
                case "updateCrossSectionYDip": {
                    viewer.updateCrossSectionYDip(param);
                    break;
                }
                case "updateCrossSectionZDip": {
                    viewer.updateCrossSectionZDip(param);
                    break;
                }
                case "updateCrossSectionWDip": {
                    viewer.updateCrossSectionCustomDip(param);
                    break;
                }
                case "updateCrossSectionXDipAzimuth": {
                    viewer.updateCrossSectionXDipAzimuth(param);
                    break;
                }
                case "updateCrossSectionYDipAzimuth": {
                    viewer.updateCrossSectionYDipAzimuth(param);
                    break;
                }
                case "updateCrossSectionZDipAzimuth": {
                    viewer.updateCrossSectionZDipAzimuth(param);
                    break;
                }
                case "updateCrossSectionWDipAzimuth": {
                    viewer.updateCrossSectionCustomDipAzimuth(param);
                    break;
                }
                case "showOutline": {
                    viewer.updateCrossSectionEnableHighlight(param.showOutline);
                    break;
                }
                case "keepPlaneVisible": {
                    viewer.enableCrossSectionFading(param.keepPlaneVisible);
                    break;
                }
                case "updateCrossSection": {
                    viewer.updateCrossSection(param);
                    break;
                }
                case "setDirection": {
                    const directionType = param;
                    switch (directionType) {
                        default:
                        case 1: //North
                            viewer.alignCameraToDirection([0.0, 1.0, 0.0]);
                            break;
                        case 2: //South
                            viewer.alignCameraToDirection([0.0, -1.0, 0.0]);
                            break;
                        case 3: //East
                            viewer.alignCameraToDirection([1.0, 0.0, 0.0]);
                            break;
                        case 4: //West
                            viewer.alignCameraToDirection([-1.0, 0.0, 0.0]);
                            break;
                        case 5: //Up
                            viewer.alignCameraToDirection([0.0, 0.0, 1.0]);
                            break;
                        case 6: //Down
                            viewer.alignCameraToDirection([0.0, 0.0, -1.0]);
                            break;
                    }
                    break;
                }
                case "sliceMode": {
                    const presetType = param;
                    ViewerActions.handleSliceMode(presetType);
                    break;
                }
            }
            ViewerActions.emit("updateViewerSettings", event);
        }
    }

    static handleSliceMode(presetType: any) {
        const viewer: any = ViewerActions.viewer;
        if (!viewer) {
            return;
        }
        switch (presetType) {
            case 0:
            default:
                break;
            case 1:
                viewer.alignCrossSectionXNormal(false);
                break;

            case 2:
                //Using flipped normal with this preset so that we align with the plane facing upwards
                viewer.alignCrossSectionZNormal(/*false*/ true);
                break;

            case 3:
                viewer.alignCrossSectionXNormal(false);
                break;

            case 4:
                viewer.alignCrossSectionYNormal(false);
                break;

            case 5:
                viewer.alignCrossSectionZNormal(false);
                break;

            case 6:
                viewer.alignCrossSectionXNormal(true);
                break;

            case 7:
                viewer.alignCrossSectionYNormal(true);
                break;

            case 8:
                viewer.alignCrossSectionZNormal(true);
                break;

            case 9:
                viewer.alignCrossSectionXNormal(false);
                break;

            case 10:
                viewer.alignCrossSectionYNormal(false);
                break;

            case 11:
                viewer.alignCrossSectionCustomNormal(false);
                break;
        }
    }

    static getInputsId(layer: any) {
        var { inputs } = ViewerEvents;
        let meta = inputs[layer.view_type];
        if (meta) {
            const input: any = Object.values(meta).find((input: any) => input.type === "3");
            if (input) {
                const input_id = input.id;
                const input_value = inputsType[input_id];
                return input_value;
            }
        }
        return;
    }

    static onLayerClick(evt: any) {
        ViewerActions.bind(this);
    }

    static onRaycastHit(event: any) {
        ViewerActions.bind(this);
    }

    static onCrossSectionChange(evt: any) {
        ViewerActions.bind(this);
    }

    static onNewScene() {
        ViewerActions.bind(this);
    }

    static onSetBusy(isBusy: boolean) {
        ViewerActions.bind(this);
    }

    static onOrthoScale(ortho: any) {
        ViewerActions.bind(this);
    }

    static updateSliders() {
        ViewerActions.bind(this);
    }

    static setCrossSectionBounds(viewBounds: any) {
        ViewerActions.viewBounds = viewBounds;
    }

    static getViewBounds() {
        return ViewerActions.viewBounds;
    }

    static getBlockModelBounds(layer: any, inputs: any) {
        var steps = ViewerActions.getStepsForLayer(layer.ID);
        var idsList: any = ViewerActions.getZoomToLayersIds([layer], inputs);
        var bounds = getEntitiesBounds(idsList, ViewerActions.project);
        var newBounds: any = deepCopyObject(bounds);
        var max: any = { x: 100, y: 100, z: 100 };

        max["x"] = (bounds.max.x - bounds.min.x) / steps[0];
        max["y"] = (bounds.max.y - bounds.min.y) / steps[1];
        max["z"] = (bounds.max.z - bounds.min.z) / steps[2];
        const initialState = getLayerSettings(
            layer,
            [
                "index_filter",
                "aabb_step_min_x",
                "aabb_step_max_x",
                "aabb_step_min_y",
                "aabb_step_max_y",
                "aabb_step_min_z",
                "aabb_step_max_z",
                "aabb_step_enable_x",
                "aabb_step_enable_y",
                "aabb_step_enable_z",
            ],
            [0, 0, 100, 0, 100, 0, 100, true, true, true]
        );
        var { aabbStepMinX, aabbStepMinY, aabbStepMinZ, aabbStepMaxX, aabbStepMaxY, aabbStepMaxZ } = initialState;
        var minAdd = {
            x: aabbStepMinX,
            y: aabbStepMinY,
            z: aabbStepMinZ,
        };
        var maxAdd = {
            x: max["x"] - aabbStepMaxX,
            y: max["y"] - aabbStepMaxY,
            z: max["z"] - aabbStepMaxZ,
        };
        //console.log('minAdd:', minAdd);
        //console.log('maxAdd:', maxAdd);
        newBounds["min"]["x"] = bounds["min"]["x"] + minAdd["x"] * steps[0];
        newBounds["max"]["x"] = bounds["max"]["x"] - maxAdd["x"] * steps[0];
        newBounds["min"]["y"] = bounds["min"]["y"] + minAdd["y"] * steps[1];
        newBounds["max"]["y"] = bounds["max"]["y"] - maxAdd["y"] * steps[1];
        newBounds["min"]["z"] = bounds["min"]["z"] + minAdd["z"] * steps[2];
        newBounds["max"]["z"] = bounds["max"]["z"] - maxAdd["z"] * steps[2];
        return newBounds;
    }

    static getLayers() {
        const viewer: any = ViewerActions.viewer;
        if (viewer) {
            return viewer.getLayers();
        }
        return [];
    }

    static getCompositeData() {
        return ViewerActions.compositeMeta;
    }

    static captureSnapshot(callback: (canvas: any) => void) {
        const viewer = ViewerActions.viewer;
        if (viewer && viewer !== null) {
            viewer.captureFrame((canvas: any) => {
                callback(canvas);
            });
        }
    }

    static getCanvas() {
        return document.querySelector("#embedded-viewer > div > canvas");
    }

    static resizeCanvas(
        canvas: any,
        width: number,
        height: number,
        callback: (prevWidth: number, prevHeight: number) => void
    ) {
        if (!canvas) {
            canvas = this.getCanvas();
        }
        if (canvas !== null) {
            var prevWidth = canvas.width;
            var prevHeight = canvas.height;
            canvas.width = width;
            canvas.height = height;
            callback(prevWidth, prevHeight);
        }
    }

    static captureViewThumbnail(callback: any) {
        const viewer = ViewerActions.viewer;
        if (viewer && viewer !== null) {
            viewer.captureFrame((canvas: any) => {
                var smallCanvas: any = document.createElement("CANVAS");
                var dim = canvas.width;
                if (canvas.height < dim) dim = canvas.height;
                smallCanvas.width = dim;
                smallCanvas.height = dim;
                var destCanvasContext = smallCanvas.getContext("2d");
                destCanvasContext.drawImage(
                    canvas,
                    -(canvas.width - smallCanvas.width) / 2,
                    -(canvas.height - smallCanvas.height) / 2
                );
                var imgData = smallCanvas.toDataURL("image/png");

                var img: any = document.createElement("IMG");
                img.width = dim;
                img.height = dim;
                img.onload = function () {
                    var iconCanvas: any = document.createElement("CANVAS");
                    iconCanvas.width = 256;
                    iconCanvas.height = 256;
                    var iconContext = iconCanvas.getContext("2d");
                    iconContext.drawImage(img, 0, 0, iconCanvas.width, iconCanvas.height);
                    var iconData = iconCanvas.toDataURL("image/jpeg");
                    var id = createGuid();
                    callback(id, iconData);
                };
                img.setAttribute("src", imgData);
            });
        }
    }

    static inherentSpecificInputs(layer: any, subLayer: any) {
        Object.entries(layer).forEach((property: any) => {
            if (property[0].includes("color_legend") || property[0].includes("colorlegend")) {
                subLayer[property[0]] = property[1];
            }
        });
        return subLayer;
    }

    static setMeshProperties(subLayer: any, meshProperty: any, update?: boolean) {
        if (update) {
            if (ViewerActions.compositeMeshes[subLayer.parent_layer]) {
                ViewerActions.compositeMeshes[subLayer.parent_layer]["subLayers"][meshProperty.meshIndex] = subLayer;
            }
            var modifiedValues: any = {
                index: subLayer.index,
                visible: subLayer.layer_visible,
                opacity: parseFloat(subLayer.layer_opacity) || 1.0,
                colorLegendMode: subLayer.color_legend_mode || "1",
                colors: [
                    typeof subLayer.color_legend_static_r !== "undefined"
                        ? parseInt(subLayer.color_legend_static_r)
                        : 128,
                    typeof subLayer.color_legend_static_g !== "undefined"
                        ? parseInt(subLayer.color_legend_static_g)
                        : 128,
                    typeof subLayer.color_legend_static_b !== "undefined"
                        ? parseInt(subLayer.color_legend_static_b)
                        : 128,
                ],
                back_colors: [
                    typeof subLayer.color_legend_static_back_r !== "undefined"
                        ? parseInt(subLayer.color_legend_static_r)
                        : 128,
                    typeof subLayer.color_legend_static_back_g !== "undefined"
                        ? parseInt(subLayer.color_legend_static_g)
                        : 128,
                    typeof subLayer.color_legend_static_back_b !== "undefined"
                        ? parseInt(subLayer.color_legend_static_b)
                        : 128,
                ],
            };

            Object.keys(ViewerActions.pendingCompositeMeshes).forEach((layerID: string) => {
                ViewerActions.pendingCompositeMeshes[layerID].forEach((submesh: any) => {
                    if (submesh && submesh.index === subLayer.index) {
                        ViewerActions.pendingCompositeMeshes[subLayer.parent_layer][subLayer.index + ""] =
                            modifiedValues;
                    } else {
                        if (!ViewerActions.pendingCompositeMeshes[subLayer.parent_layer]) {
                            ViewerActions.pendingCompositeMeshes[subLayer.parent_layer] = [];
                        }
                        ViewerActions.pendingCompositeMeshes[subLayer.parent_layer][subLayer.index + ""] =
                            modifiedValues;
                    }
                });
            });
        }
        //console.log('pendingCompositeMeshes:',ViewerActions.pendingCompositeMeshes);

        if (ViewerActions.viewer) {
            ViewerActions.viewer.setMeshPropertiesAt(
                meshProperty.parentLayer,
                meshProperty.meshIndex,
                meshProperty.meshVisible,
                meshProperty.meshOpacity,
                meshProperty.meshUseParentColorLegend,
                meshProperty.meshStaticColorFront,
                meshProperty.meshStaticColorBack
            );
        }
    }

    static allowedSaveType(type: string) {
        //var allowedType: string = 'folder, raster';
        //return allowedType.includes(type);
        return true;
    }

    static loadDefaultValues(values: any, currentProject: any, done: (values: any, loadMetaData: boolean) => void) {
        if (values["view_type"] === "com.voxelfarm.program.view.planegenerator") {
            var { a, b, c, d } = getPlaneInputValues(0);
            values["input_value_plane_a"] = a;
            values["input_value_plane_b"] = b;
            values["input_value_plane_c"] = c;
            values["input_value_plane_d"] = d;

            if (values["ortho_list"] && !values["raster_list"]) {
                var rasters: any = values["ortho_list"].trim().split(" ");
                var rastersCount = rasters.length;
                var orthoList: any = [];
                var rastersList: any = [];
                var rasterSetList: any = [];
                rasters.forEach((rasterId: string) => {
                    var raster = currentProject[rasterId];
                    if (raster) {
                        if (raster["file_type"] === "ORTHOSET" && raster["raster_list"]) {
                            RasterActions.addRaster(raster, values, (rasterSet: any) => {
                                rasterSetList.push(rasterSet.ID);
                                rastersCount--;
                                if (rastersCount === 0) {
                                    values["ortho_list"] = orthoList.join(" ") + " " + rasterSet["ortho_list"];
                                    values["raster_list"] = rastersList.join(" ") + " " + rasterSet["raster_list"];
                                    values["rasterset_list"] = rasterSetList.join(" ");
                                    done(values, true);
                                }
                            });
                        } else {
                            RasterActions.addRaster(raster, undefined, (selectedRaster: any) => {
                                rastersList.push(selectedRaster.ID);
                                orthoList.push(raster.ID);
                                rastersCount--;
                                if (rastersCount === 0) {
                                    values["raster_list"] = rastersList.join(" ");
                                    done(values, true);
                                }
                            });
                        }
                    }
                });
            }
        } else {
            done(values, false);
        }
    }
    static getZoomToLayersIds(layers: any, inputs: any) {
        let attributeZoom: any = [],
            attributeZoomEntities: any = [];

        if (layers) {
            Object.values(layers).forEach((layer: any) => {
                if (inputs && inputs[layer.view_type]) {
                    attributeZoom = Object.values(inputs[layer.view_type]).filter((elem: any) => elem.type === "3");
                    attributeZoom.forEach((input: any) => {
                        let inputId: string = input.id;
                        attributeZoomEntities.push(layer["input_value_" + inputId]);
                    });
                }

                if (layer["ortho_list"]) {
                    var ortho_list = layer["ortho_list"].split(" ");
                    ortho_list.forEach((ortho: string) => {
                        attributeZoomEntities.push(ortho);
                    });
                }
            });
        }

        return attributeZoomEntities;
    }

    static hasBounds() {
        var viewBounds = ViewerActions.getViewBounds();
        var bounds: any = Object.getOwnPropertyNames(viewBounds).find((entry: any) => entry === "0");
        return bounds !== undefined;
    }

    static getStepsForLayer(layerId: string) {
        var steps = [3.0, 3.0, 3.0];
        var viewBounds = ViewerActions.getViewBounds();
        var bounds: any = Object.values(viewBounds).find((entry: any) => entry && entry.id === layerId);
        if (bounds) {
            steps = bounds.blocks[0];
        }

        return steps;
    }

    static getPolygonPoints(layer: any) {
        var viewer = ViewerActions.viewer;
        var vfPoints: any = [],
            cwPoints: any = [];
        if (typeof layer["polygon_points"] === "string") {
            var wPoints = layer["polygon_points"].split(",");
            if (viewer && viewer._renderInterface) {
                var currentPoint: any = [];
                wPoints.forEach((point: string, index: number) => {
                    currentPoint.push(parseFloat(point));
                    if (index > 0 && index % 3 === 2) {
                        cwPoints.push(currentPoint);
                        currentPoint = [];
                    }
                });
                cwPoints.forEach((point: number) => {
                    vfPoints.push(viewer._renderInterface.affineTransform.WC_TO_VF(point));
                });
            }
        }
        return { vfPoints: vfPoints, wPoints: [] };
    }

    static getRasterLayer(raster: any) {
        var layer;
        Object.entries(ViewerActions.compositeRasters).forEach((entry: any) => {
            var layerId = entry[0];
            var rasters = entry[1];
            rasters.forEach((curRaster: any) => {
                if (curRaster.ID === raster.ID) {
                    layer = ViewerActions.project[layerId];
                }
            });
        });
        return layer;
    }
}
